import type { QueryHookOptions, MutationHookOptions } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client';
import { AnalyticsEvents } from '$util/analytics/types';
import { recordAnalytics } from '$util/analytics';
import { useSession } from '$apollo/gql/session/hooks';
import type {
  ICustomer,
  IQueryGetCustomerVehicles,
  IQueryGetCustomerBookings,
  IDeleteCustomerVehicle,
  IQueryGetCustomerData,
  MutationCreateCustomerParams,
  MutationUpdateCustomerParams,
  QueryGetCustomerVariables,
  MutationCreateCustomerVariables,
  MutationUpdateCustomerVariables,
  MutationCreateCallBackRequestParams,
} from './index';
import {
  MUTATION_ADD_REMINDER,
  MUTATION_CREATE_CALL_BACK_REQUEST,
  QUERY_GET_CUSTOMER_VEHICLES,
  QUERY_GET_CUSTOMER_BOOKINGS,
  MUTATION_DELETE_CUSTOMER_VEHICLE,
  QUERY_GET_CUSTOMER,
  MUTATION_CREATE_CUSTOMER,
  MUTATION_UPDATE_CUSTOMER,
} from './index';

type CreateCustomerMutationOptions = MutationHookOptions<
  MutationCreateCustomerParams,
  MutationCreateCustomerVariables
>;

type UpdateCustomerMutationOptions = MutationHookOptions<
  MutationUpdateCustomerParams,
  MutationUpdateCustomerVariables
>;

/**
 * Query options to use with apollo hooks, cache or ssr queries
 */
export const getCustomerQueryOptions = (customerId: ICustomer['id']): QueryHookOptions => ({
  variables: { customerId },
});

export const useCustomer = () => {
  const { data: session } = useSession();
  const { customerId } = session;
  const queryResult = useQuery<IQueryGetCustomerData, QueryGetCustomerVariables>(QUERY_GET_CUSTOMER, {
    variables: { customerId },
    skip: !customerId,
    ssr: false,
  });
  return queryResult;
};

export const useCreateCustomer = (options?: CreateCustomerMutationOptions) => {
  const [mutationCreateCustomer, result] = useMutation<
    MutationCreateCustomerParams,
    MutationCreateCustomerVariables
  >(MUTATION_CREATE_CUSTOMER, options);

  const createCustomer = (customer: MutationCreateCustomerParams) => {
    mutationCreateCustomer({ variables: { customerInput: customer } });
  };

  return [createCustomer, result] as const;
};

export const useUpdateCustomer = (options?: UpdateCustomerMutationOptions) => {
  const [mutationUpdateCustomer, result] = useMutation<
    MutationUpdateCustomerParams,
    MutationUpdateCustomerVariables
  >(MUTATION_UPDATE_CUSTOMER, options);

  const updateCustomer = (customer: MutationUpdateCustomerParams) => {
    mutationUpdateCustomer({ variables: { updateCustomerInput: customer } });
  };

  return [updateCustomer, result] as const;
};

/**
 * Hook to get customer vehicles
 */
export const useCustomerVehicles = (customerId: string) => {
  const { loading, data } = useQuery<IQueryGetCustomerVehicles>(QUERY_GET_CUSTOMER_VEHICLES, {
    variables: { customerId },
    skip: !customerId,
    ssr: false,
  });
  return { data: data?.getCustomerVehicles || [], loading };
};

const sendCallbackSuccessAnalytics = () => recordAnalytics(AnalyticsEvents.CALLBACK_SUCCESS);

const sendMotReminderSuccessAnalytics = () => recordAnalytics(AnalyticsEvents.MOT_REMINDER_SUCCESS);
const sendMotReminderErrorAnalytics = (err: { message: string }) =>
  recordAnalytics(AnalyticsEvents.MOT_REMINDER_ERROR, err.message);

export const useAddReminder = () => {
  const [mutationAddReminder, res] = useMutation(MUTATION_ADD_REMINDER, {
    onCompleted: sendMotReminderSuccessAnalytics,
    onError: sendMotReminderErrorAnalytics,
  });
  const {
    data: { vehicleId },
  } = useSession();
  const addReminder = (email: string) =>
    mutationAddReminder({
      variables: {
        email,
        vehicleId,
        reminderEmail: true,
      },
    });
  return [addReminder, res] as const;
};

export const useDeleteCustomerVehicle = (customerId: string) => {
  const [mutationDeleteCustomerVehicle, res] = useMutation<IDeleteCustomerVehicle>(
    MUTATION_DELETE_CUSTOMER_VEHICLE,
    {
      update: (cache, { data }) => {
        const { vehicleId: deletedVehicleId } = data?.deleteCustomerVehicle || {};
        const existingVehicles = cache.readQuery<IQueryGetCustomerVehicles>({
          query: QUERY_GET_CUSTOMER_VEHICLES,
          variables: { customerId },
        });
        const updatedVehicles =
          existingVehicles?.getCustomerVehicles.filter(
            ({ vehicleId: cacheVehicleId }) => cacheVehicleId !== deletedVehicleId
          ) || [];
        cache.writeQuery<IQueryGetCustomerVehicles>({
          query: QUERY_GET_CUSTOMER_VEHICLES,
          variables: { customerId },
          data: { getCustomerVehicles: updatedVehicles },
        });
      },
    }
  );
  const deleteCustomerVehicle = (vehicleId: string) => {
    if (!vehicleId) return false;
    return mutationDeleteCustomerVehicle({
      variables: {
        vehicleId,
      },
    });
  };
  return [deleteCustomerVehicle, res] as const;
};

export const useCreateCallBackRequest = () => {
  const [mutationCreateCallBackRequest, res] = useMutation(MUTATION_CREATE_CALL_BACK_REQUEST, {
    onCompleted: sendCallbackSuccessAnalytics,
  });
  const createCallBackRequest = (params: MutationCreateCallBackRequestParams) =>
    mutationCreateCallBackRequest({
      variables: { createCallBackRequestInput: params },
    });
  return [createCallBackRequest, res] as const;
};

/**
 * Hook to get customer bookings
 */
export const useCustomerBookings = () => {
  const { data, ...res } = useQuery<IQueryGetCustomerBookings>(QUERY_GET_CUSTOMER_BOOKINGS, {
    variables: { filter: { masterStatusId_gt: 500 } },
    ssr: false,
  });
  return { data: data?.getCustomerBookings, ...res };
};
